import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router';
import { ApiError } from '../api/client';
import { ops } from '../api/operations';
import type { AuthCapabilities } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { Mark, Wordmark } from '../shell/Brand';

/**
 * What the sign-in and sign-up pages can offer (`GET /v1/auth/capabilities`, anonymous; M7 slice 3): "Forgot password?"
 * only when some organization has SMTP, the sign-up link only while sign-up is open. Null until read (or when the read
 * fails): the sign-up link shows (the server says itself when sign-up is closed), the reset link does not.
 */
export function useAuthCapabilities(): AuthCapabilities | null {
  const { client } = useAuth();
  const [caps, setCaps] = useState<AuthCapabilities | null>(null);
  useEffect(() => {
    let cancelled = false;
    client.data(ops.getAuthCapabilities, { auth: false }).then(
      (c) => !cancelled && setCaps(c),
      () => undefined,
    );
    return () => {
      cancelled = true;
    };
  }, [client]);
  return caps;
}

/** The mark and the wordmark above every sign-in family form. */
export function SignInBrand() {
  return (
    <div className="signin-brand">
      <Mark size={32} />
      <Wordmark />
    </div>
  );
}

export function SignIn() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const invite = params.get('invite');
  const caps = useAuthCapabilities();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signIn(email, password);
      // an invite link sent the caller here to sign in as its email: back to accept it
      const from = invite ? `/accept-invite?token=${encodeURIComponent(invite)}` : ((location.state as { from?: string } | null)?.from ?? '/orgs');
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.status === 401 ? 'Invalid email or password.' : `${err.message} (request ${err.requestId})`);
      } else {
        setError('Something went wrong.');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="signin">
      <form onSubmit={onSubmit} className="card" aria-labelledby="signin-title">
        <SignInBrand />
        <h1 id="signin-title">Sign in</h1>
        <p className="lede" role={invite ? 'note' : undefined}>
          {invite ? 'Sign in to accept the invite.' : 'The content and LiveOps of your games.'}
        </p>
        <label>
          Email
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" required autoFocus />
        </label>
        <label>
          Password
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required />
        </label>
        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
        <button type="submit" disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
        <div className="links">
          {caps?.passwordReset && (
            <p>
              <Link to="/forgot-password">Forgot password?</Link>
            </p>
          )}
          {caps?.signUp !== false && (
            <p>
              New here? <Link to="/sign-up">Create an account</Link>
            </p>
          )}
        </div>
      </form>
    </div>
  );
}
